import Vue from "vue"
import Vuex from "vuex"
import pathify from "@/store/pathify"
import createPersistedState from "vuex-persistedstate"
import createLogger from "vuex/dist/logger"

import account from "@/store/account"
import ui from "@/store/ui"

// Obfuscation key for localStorage data
const OBFUSCATION_KEY = 'sjRHejZ243k7917NEdvZ6XlvDK'

Vue.use(Vuex)

// Only enable pathify debug in development
if (process.env.NODE_ENV === 'development') {
  pathify.debug()
}

// Simple obfuscation functions
const obfuscateData = (data) => {
  try {
    const jsonString = JSON.stringify(data)
    // Simple XOR obfuscation with a key
    let obfuscated = ''
    for (let i = 0; i < jsonString.length; i++) {
      obfuscated += String.fromCharCode(jsonString.charCodeAt(i) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length))
    }
    // Convert to base64 for additional obfuscation
    return btoa(obfuscated)
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error obfuscating data:', error)
    }
    return data
  }
}

const deobfuscateData = (obfuscatedData) => {
  try {
    // Decode from base64
    const decoded = atob(obfuscatedData)
    // XOR deobfuscation
    let deobfuscated = ''
    for (let i = 0; i < decoded.length; i++) {
      deobfuscated += String.fromCharCode(decoded.charCodeAt(i) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length))
    }
    return JSON.parse(deobfuscated)
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error deobfuscating data:', error)
    }
    // Fallback: try to parse as regular JSON (for backward compatibility)
    try {
      return JSON.parse(obfuscatedData)
    } catch (fallbackError) {
      return null
    }
  }
}

// Enhanced localStorage configuration with error handling, data validation, and obfuscation
const createPersistedStateWithValidation = () => {
  return createPersistedState({
    key: 'faculty-recruitment-app',
    storage: window.localStorage,
    paths: ['account', 'ui'],
    
    // Data validation before saving
    beforeRestore: (context) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('Restoring state from localStorage...')
      }
    },
    
    // Data validation after restoring
    afterRestore: (context) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('State restored from localStorage')
      }
      // Validate and sanitize restored data
      validateAndSanitizeData(context.store)
    },
    
    // Error handling for storage operations with obfuscation
    storage: {
      getItem: (key) => {
        try {
          const item = window.localStorage.getItem(key)
          if (!item) return null
          
          // Try to deobfuscate the data
          const deobfuscated = deobfuscateData(item)
          return deobfuscated
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error reading from localStorage:', error)
          }
          return null
        }
      },
      setItem: (key, value) => {
        try {
          // Obfuscate the data before storing
          const obfuscated = obfuscateData(value)
          window.localStorage.setItem(key, obfuscated)
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error writing to localStorage:', error)
          }
          // Handle quota exceeded error
          if (error.name === 'QuotaExceededError') {
            clearOldData()
            try {
              const obfuscated = obfuscateData(value)
              window.localStorage.setItem(key, obfuscated)
            } catch (retryError) {
              if (process.env.NODE_ENV === 'development') {
                console.error('Failed to save data even after clearing old data:', retryError)
              }
            }
          }
        }
      },
      removeItem: (key) => {
        try {
          window.localStorage.removeItem(key)
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error removing from localStorage:', error)
          }
        }
      }
    }
  })
}

// Data validation and sanitization
const validateAndSanitizeData = (store) => {
  try {
    // Validate account data
    if (store.state.account) {
      const account = store.state.account
      if (typeof account.isAuthenticated !== 'boolean') {
        store.set('account/isAuthenticated', false)
      }
      if (account.emailAddress && typeof account.emailAddress !== 'string') {
        store.set('account/emailAddress', null)
      }
    }

    // Validate UI data
    if (store.state.ui) {
      const ui = store.state.ui
      
      // Ensure arrays are actually arrays
      const arrayFields = ['employmentPostPhD', 'employmentPrePhD', 'books', 'papers', 'research', 'consulting', 'awards']
      arrayFields.forEach(field => {
        if (!Array.isArray(ui[field])) {
          store.set(`ui/${field}`, [])
        }
      })

      // Ensure objects are actually objects
      const objectFields = ['bachelors', 'masters', 'application', 'contact', 'thesis', 'personal', 'referee1', 'referee2', 'certificates']
      objectFields.forEach(field => {
        if (typeof ui[field] !== 'object' || ui[field] === null) {
          store.set(`ui/${field}`, {})
        }
      })

      // Ensure booleans are actually booleans
      const booleanFields = ['showNavbar', 'showFooter', 'showEmploymentForm', 'showBookForm', 'showPaperForm', 'showResearchForm', 'showConsultingForm', 'isPostPhD']
      booleanFields.forEach(field => {
        if (typeof ui[field] !== 'boolean') {
          store.set(`ui/${field}`, field.startsWith('show') ? true : false)
        }
      })
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error validating restored data:', error)
    }
    // Reset store to initial state if validation fails
    store.commit('account/reset')
    store.commit('ui/reset')
  }
}

// Clear old data when storage quota is exceeded
const clearOldData = () => {
  try {
    const keys = Object.keys(window.localStorage)
    const appKeys = keys.filter(key => key.startsWith('faculty-recruitment-app'))
    
    // Keep only the most recent data
    if (appKeys.length > 1) {
      appKeys.slice(0, -1).forEach(key => {
        window.localStorage.removeItem(key)
      })
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error clearing old data:', error)
    }
  }
}

// Migration function for future schema changes
const migrateData = (store) => {
  const version = store.get('ui/dataVersion') || 1
  const currentVersion = 1

  if (version < currentVersion) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`Migrating data from version ${version} to ${currentVersion}`)
    }
    
    // Add migration logic here when needed
    // Example:
    // if (version < 2) {
    //   // Migrate from version 1 to 2
    // }
    
    // Update version
    store.set('ui/dataVersion', currentVersion)
  }
}

const store = {
  modules: {
    account: { ...account },
    ui: { ...ui }
  },
  
  // Global state for app-wide data
  state: {
    appVersion: '1.0.0',
    lastSaved: null,
    isOnline: navigator.onLine,
    storageQuota: {
      used: 0,
      total: 0
    }
  },
  
  mutations: {
    setLastSaved(state, timestamp) {
      state.lastSaved = timestamp
    },
    setIsOnline(state, isOnline) {
      state.isOnline = isOnline
    },
    setStorageQuota(state, quota) {
      state.storageQuota = quota
    }
  },
  
  actions: {
    // Check storage quota
    async checkStorageQuota({ commit }) {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        try {
          const estimate = await navigator.storage.estimate()
          commit('setStorageQuota', {
            used: estimate.usage || 0,
            total: estimate.quota || 0
          })
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error checking storage quota:', error)
          }
        }
      }
    },
    
    // Save data with timestamp
    saveData({ commit }) {
      commit('setLastSaved', new Date().toISOString())
    },
    
    // Initialize app
    async initializeApp({ dispatch, commit }) {
      // Check online status
      window.addEventListener('online', () => commit('setIsOnline', true))
      window.addEventListener('offline', () => commit('setIsOnline', false))
      
      // Check storage quota
      await dispatch('checkStorageQuota')
      
      // Save initial timestamp
      dispatch('saveData')
    }
  },
  
  getters: {
    storageUsagePercentage: state => {
      if (state.storageQuota.total === 0) return 0
      return Math.round((state.storageQuota.used / state.storageQuota.total) * 100)
    },
    
    isStorageFull: (state, getters) => {
      return getters.storageUsagePercentage > 90
    }
  }
}

// Enhanced plugins configuration
const plugins = []

// Add persistence plugin
plugins.push(createPersistedStateWithValidation())

// Add pathify plugin
plugins.push(pathify.plugin)

// Add logger in development only
if (process.env.NODE_ENV === 'development') {
  plugins.push(createLogger({
    collapsed: false,
    logMutations: true,
    logActions: true
  }))
}

export default new Vuex.Store({
  plugins,
  strict: process.env.NODE_ENV === "development",
  ...store,
})
